import { useState, useEffect, useCallback } from "react";
import useAxios from "./useAxios";
import Constants from "../Constants";
import { toaster } from "../utils/toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useConfirm } from "../contexts/ConfirmContext";
import { DISCOVERY_OBJECTION_GROUP_MAP } from "@demandproai/shared";
import { useDiscoveryOverviewContext } from "../contexts/DiscoveryOverviewContext";

const validationSchema = Yup.object({
    text: Yup.string()
        .trim()
        .required('Objection text is required')
        .max(2000, 'Objection text is too long'),
    // only a new objection declares its types, an edit just changes the text
    discoveryTypes: Yup.array().when('objectionId', {
        is: (objectionId) => !objectionId,
        then: (schema) => schema.min(1, 'Select at least one discovery type'),
    }),
});

const useDiscoveryObjections = () => {
    const { discoveryCaseInfo, selectedDiscoveryObj } = useDiscoveryOverviewContext()
    const discoveryType = selectedDiscoveryObj?.value

    const { getData, isLoading ,postData, deleteData} = useAxios();
    const confirm = useConfirm();
    const [objectionGroups, setObjectionGroups] = useState([]);
    const [isObjectionModalOpen, setIsObjectionModalOpen] = useState(false)

     const formik = useFormik({
        initialValues: { objectionId: '', text: '', discoveryTypes: [] },
        validationSchema,
        onSubmit: (values) => updateObjection(values)
    })

    const objectionId = formik.values?.objectionId;
    const selectedDiscoveryType = formik.values.discoveryTypes;

    const fetchObjections = useCallback(async () => {
        // wait for the discovery type so the bank is never shown unfiltered
        if (!discoveryType) return;

        const query = new URLSearchParams({ discoveryType }).toString();
        const resp = await getData(`${Constants.ApiUrl.discovery.getDiscoveryObjectionGroup}?${query}`);
        const { success, data, message } = resp || {};
        if (!success) {
            toaster({ message: message || "Failed to load objections", success: false });
            return;
        }
        setObjectionGroups(data ?? []);
    }, [getData, discoveryType]);

    useEffect(() => {
        fetchObjections();
    }, [fetchObjections]);

    const openObjectionModal = (item) => {
        const editingObjectionId = item?.objectionId || ''
        // a new objection defaults to the discovery type being responded to
        const shouldPreselectType = !editingObjectionId && Boolean(discoveryType)
        // resetForm clears errors and touched left over from the previous open
        formik.resetForm({
            values: {
                objectionId: editingObjectionId,
                text: item?.objectionText || '',
                discoveryTypes: shouldPreselectType ? [discoveryType] : []
            }
        })
        setIsObjectionModalOpen(true)
    }

    // the dropdown never blurs, so touch it here to surface its error
    const setSelectedDiscoveryType = useCallback((types) => {
        formik.setFieldTouched('discoveryTypes', true, false)
        formik.setFieldValue('discoveryTypes', types)
    }, [formik])

    const getErrorMessage = useCallback(
        (field) => {
            const meta = formik.getFieldMeta(field)
            return meta.touched && meta.error
        },
        [formik],
    )

    const updateObjection = useCallback(async (value) => {
        // validation trims only for its own check, so trim again before saving
        const text = value.text.trim()
        const payload = value.objectionId
            ? { text }
            : { text, discoveryTypes: value.discoveryTypes, group: DISCOVERY_OBJECTION_GROUP_MAP.CUSTOM_OBJECTIONS.value, state: discoveryCaseInfo?.stateOfIncident }

        const { success } = await postData(`${Constants.ApiUrl.discovery.addEditDiscoveryObjection}/${value.objectionId}`, payload)
        if (success) {
            fetchObjections()
            setIsObjectionModalOpen(false)
        }
    }, [postData, fetchObjections, discoveryCaseInfo?.stateOfIncident])


    
    const deleteDiscoveryObjection = useCallback(async (objectionId) => {
        const isConfirmed = await confirm({
            title: "Delete this objection?",
            description: "This objection will be removed from the objection bank.",
            confirmText: "Delete",
            cancelText: "Cancel",
        })
        if (!isConfirmed) return

        const { success, message } = await deleteData(`${Constants.ApiUrl.discovery.deleteDiscoveryObjection}/${objectionId}`)
        if (success) {
            fetchObjections()
            toaster({ message, success: true })

        }
    }, [confirm, deleteData, fetchObjections])

    const modalTitle = formik.values.objectionId ? 'Edit Objection' : 'Add Objection'

    return {
        objectionId,
        objectionGroups,
        isLoading,
        isObjectionModalOpen,
        formik,
        selectedDiscoveryType,
        modalTitle,
        getErrorMessage,
        setIsObjectionModalOpen,
        openObjectionModal,
        deleteDiscoveryObjection,
        setSelectedDiscoveryType
    };
};

export default useDiscoveryObjections;
